<script setup lang="ts">
import { computed, defineAsyncComponent, onBeforeMount, reactive } from 'vue';
import { onBeforeRouteUpdate } from 'vue-router';
import { useHead } from '@unhead/vue';
import { useEventListener } from '@vueuse/core';
import { show404Page } from '~/utilities/show-404-page';
import { useProjectStore } from '~/stores/project-store';
import { useVfsStore } from '~/stores/vfs';
import { useEditorStore } from '~/stores/editor-store';
import Spinner from '~/components/Spinner.vue';

// Populated by the Vue Router
interface Props {
  usernameOrUserId: string;
  projectSlugOrId: string;
  version: string;
}

const props = defineProps<Props>();
const projectStore = useProjectStore();
const vfsStore = useVfsStore();
const editorStore = useEditorStore();
const pyscriptCDN = 'https://pyscript.net/releases/2024.11.1/core.js';
const ProjectPageEditable = defineAsyncComponent(
  () => import('~/views/projects/components/ProjectPageEditable.vue'),
);

const ProjectPageReadOnly = defineAsyncComponent(
  () => import('~/views/projects/components/ProjectPageReadOnly.vue'),
);

const NotFoundView = defineAsyncComponent(() => import('~/views/NotFoundView.vue'));

async function pageTitle() {
  if (projectStore.project) {
    const verb = projectStore.isProjectOwner ? 'Edit' : 'View';
    return `${verb} - ${projectStore.project?.name}`;
  }
  return '';
}

// Set the version data specifically for the current route
const versionData = computed(() => projectStore.versions.find((v) => v.version === props.version));

useHead({ title: pageTitle() });

const state = reactive({
  loading: true,
});

useEventListener(window, 'dragover', (e) => e.preventDefault());
useEventListener(window, 'drop', (e) => e.preventDefault());

onBeforeMount(async () => {

  // TODO: This shuold be removed since we can load the pyscript CDN in the main index file???
  // await import(pyscriptCDN);
  try {
    // Reset the stores if not the same project previously loaded.
    if (
      projectStore.project?.slug !== props.projectSlugOrId ||
      projectStore.project?.id !== props.projectSlugOrId
    ) {
      resetStores();
    }

    state.loading = true;

    // Set up the store with data for the project and its versions.
    await projectStore.fetchProjectAndVersions(props.usernameOrUserId, props.projectSlugOrId);

    // Poll for the window.lab to be initialized from the labView.py
    // TODO: (FP) This can probably be optimized to reduce this need...
    // while (!window.lab) {
    //   // Create a delay before checking again.
    //   await new Promise((resolve) => setTimeout(resolve, 50));
    // }
    // if (!window.lab.initialized) {
    //   window.lab.initialize(vfsStore, editorStore);
    // }
  } catch (error) {
    show404Page();
  } finally {
    state.loading = false;
  }
});

/**
 * A route update occurs when the route changes, but the component stays mounted.
 * This may occur when we're renaming a project, creating a new project, or
 * forking a project while already on the project page
 */
onBeforeRouteUpdate(async (to, from) => {
  // Don't need to refetch the data if the users was just on this page
  if (to.params.projectSlugOrId !== from.params.projectSlugOrId) {
    // Reset the stores if not the same project previously loaded.
    if (
      projectStore.project?.slug !== props.projectSlugOrId ||
      projectStore.project?.id !== props.projectSlugOrId
    ) {
      resetStores();

      try {
        state.loading = true;

        await projectStore.fetchProjectAndVersions(
          to.params.usernameOrUserId as string,
          to.params.projectSlugOrId as string,
        );
      } catch (error) {
        show404Page();
      } finally {
        state.loading = false;
      }
    }
  }
});

/**
 * Completely reset the store's data.
 */
function resetStores() {
  projectStore.$reset();
  vfsStore.$reset();
}
</script>

<template>
  <div v-if="state.loading" class="flex h-full items-center justify-center text-gray-600">
    <Spinner class="h-8 w-8" />
  </div>

  <ProjectPageEditable
    v-else-if="projectStore.project && projectStore.isProjectOwner && versionData"
    :project-data="projectStore.project"
    :version-data="versionData"
  />

  <ProjectPageReadOnly
    v-else-if="projectStore.project && versionData?.published"
    :project-data="projectStore.project"
    :version-data="versionData"
  />

  <NotFoundView v-else />
</template>
